import React, {Fragment, useEffect, useState} from 'react';
import {compose} from "redux";
import {translate} from "react-i18next";
import {connect} from "react-redux";
import ELDataStep from "./data-step";
import ELConnectionStep from "./connection-step";
import ELFilterStep from "./filter-step";
import "./style.css";
import {Button, Card, Col, Icon, Modal, Row, Steps} from "antd";
import {CSS_POINTER, MARGIN_MD} from "../../styles/constants";
import { readASTTerritorialCodelistConcept, setELStep} from "./actions";
import {isArtefactValid} from "../../utils/artefactValidators";
import ELLoadStep from "./load-step";
import {
  EL_MODE_CREATE,
  EL_MODE_UPDATE,
  EL_SOURCE_FORMAT_SDMX_EP,
  EL_STEP_ATTRIBUTES,
  EL_STEP_CONNECTION,
  EL_STEP_DATA,
  EL_STEP_DIMENSIONS,
  EL_STEP_FILTER,
  EL_STEP_LOAD,
  EL_STEP_MAPPING,
  EL_STEP_SEMANTIC,
  EL_STEP_STRUCTURE
} from "./constants";
import ELStructureStep from "./structure-step";
import ELAttributesStep from "./attributes-step";
import ELSemanticStep from "./semantic-step";
import ELDimensionsStep from "./dimensions-step";
import ELMappingStep from "./mapping-step";
import { structureIsWellDefined } from './structure-step/utils';
import { dimensionsAreWellDefined } from './dimensions-step/utils';
import { attributesAreWellDefined } from './attributes-step/utils';
import { semanticIsWellDefined } from './semantic-step/utils';
import { mappingIsValid } from './mapping-step/utils';
import Call from '../../hocs/call';
import { PATH_NODES_CONFIGURATION, PATH_SET_PERMISSIONS } from '../../constants/paths';

const mapStateToProps = state => ({
  endpoints: state.config.nodes,
  endpointId: state.app.endpointId,
  step: state.scenes.easyLoading.step,
  dstDataflow: state.scenes.easyLoading.dstDataflow,
  category: state.scenes.easyLoading.category,
  forbiddenCategoriesCodes: state.scenes.easyLoading.forbiddenCategoriesCodes,
  isConnectionEndpointAvailable: state.scenes.easyLoading.isConnectionEndpointAvailable,
  sourceFormat: state.scenes.easyLoading.sourceFormat,
  srcDataflow: state.scenes.easyLoading.srcDataflow,
  srcFile: state.scenes.easyLoading.srcFile,
  structureColumns: state.scenes.easyLoading.structureColumns,
  codelists: state.scenes.easyLoading.structureCodelists,
  components: state.scenes.easyLoading.components,
  cube: state.scenes.easyLoading.cube,
  permissions: state.app.user.permissions,
  astTerritorialCodelist: state.scenes.easyLoading.astTerritorialCodelist,
  astTerritoryConceptScheme: state.scenes.easyLoading.astTerritoryConceptScheme,
  astTerritoryConcept: state.scenes.easyLoading.astTerritoryConcept,
});

const mapDispatchToProps = dispatch => ({
  onStepSet: step => dispatch(setELStep(step)),
  onReadASTTerritorialConfig: (codelist, conceptScheme, concept) => 
    dispatch(readASTTerritorialCodelistConcept(codelist, conceptScheme, concept)),
});

const EasyLoading = ({
                       t,
                       mode,
                       endpoints,
                       endpointId,
                       step,
                       dstDataflow,
                       category,
                       forbiddenCategoriesCodes,
                       isConnectionEndpointAvailable,
                       sourceFormat,
                       srcDataflow,
                       srcFile,
                       structureColumns,
                       codelists,
                       components, 
                       cube, 
                       permissions,
                       astTerritorialCodelist,  
                       astTerritoryConceptScheme, 
                       astTerritoryConcept,              
                       onStepSet,
                       onReadASTTerritorialConfig
                     }) => {

  const [steps, setSteps] = useState(null);
  const [astEnabled, setAstEnabled] = useState(false);
  const [userHasASTPermission, setUserHasASTPermission] = useState(false);

  useEffect( () => {
    const nodeASTConfig = endpoints.filter(endpoint => endpoint.general.id === endpointId)[0].ast;
    const isAstEnabled = nodeASTConfig ? nodeASTConfig.enabled : false;
    setAstEnabled(isAstEnabled);
    const hasASTPermission = (permissions.agencies || []).includes('AST');
    setUserHasASTPermission(hasASTPermission);
  }, [endpoints, endpointId, permissions]);

  useEffect( () => {
    if (astEnabled && userHasASTPermission && astTerritorialCodelist === null) {
      const nodeASTConfig = endpoints.filter(endpoint => endpoint.general.id === endpointId)[0].ast;
      const astTerritorialCodelist = nodeASTConfig.astTerritorialCodelist;
      const astTerritoryConceptScheme = nodeASTConfig.astTerritoryConceptScheme;
      const astTerritoryConcept = nodeASTConfig.astTerritoryConcept;
      onReadASTTerritorialConfig(astTerritorialCodelist, astTerritoryConceptScheme, astTerritoryConcept);
    }
  }, [endpoints, endpointId, astTerritorialCodelist, astEnabled, userHasASTPermission ]);


  useEffect(() => {
    let newSteps;

    if (mode === EL_MODE_CREATE) {
      if (sourceFormat === EL_SOURCE_FORMAT_SDMX_EP) {
        newSteps = [EL_STEP_DATA, EL_STEP_CONNECTION, EL_STEP_FILTER, EL_STEP_LOAD];
      } else {
        newSteps = [EL_STEP_DATA, EL_STEP_CONNECTION, EL_STEP_FILTER, EL_STEP_STRUCTURE, EL_STEP_DIMENSIONS, EL_STEP_ATTRIBUTES, EL_STEP_SEMANTIC, EL_STEP_LOAD];
      }
    } else {
      if (sourceFormat === EL_SOURCE_FORMAT_SDMX_EP) {
        newSteps = [EL_STEP_DATA, EL_STEP_CONNECTION, EL_STEP_FILTER, EL_STEP_LOAD];
      } else {
        newSteps = [EL_STEP_DATA, EL_STEP_CONNECTION, EL_STEP_FILTER, EL_STEP_MAPPING, EL_STEP_LOAD];
      }
    }

    setSteps(newSteps || []);
  }, [mode, sourceFormat])

  let validationResult = null;

  const isStepValid = step => {
    validationResult = null;
    switch (step) {
      case EL_STEP_DATA:
        return (
          isArtefactValid(dstDataflow) && (
            mode === EL_MODE_UPDATE ||
            (category !== null && !(forbiddenCategoriesCodes || []).includes(category.CatCode))
          )
        );
      case EL_STEP_CONNECTION:
        if (sourceFormat === EL_SOURCE_FORMAT_SDMX_EP) {
          return isConnectionEndpointAvailable && srcDataflow;
        } else {
          return srcFile && srcFile.csvPath;
        }
      case EL_STEP_FILTER:
        return true;
      case EL_STEP_STRUCTURE:
        validationResult = structureIsWellDefined(t, structureColumns);
        return validationResult.result;
      case EL_STEP_DIMENSIONS:
          validationResult = dimensionsAreWellDefined(t, structureColumns, codelists);
          return validationResult.result;
      case EL_STEP_ATTRIBUTES:
        return attributesAreWellDefined(structureColumns);
      case EL_STEP_SEMANTIC:
        return semanticIsWellDefined(structureColumns);
      case EL_STEP_MAPPING:
        return mappingIsValid(components, cube.components);
      default:
        return false;
    }
  };

  const setStep = (step, steps) => {
    
    if (steps.slice(0, steps.indexOf(step)).find(s => !isStepValid(s)) === undefined) {
      onStepSet(step);
    } else {

      const hasValidationMessages = validationResult !== null && (validationResult.messages || []).length > 0;
      
      Modal.warning({
        title: t('commons.alerts.invalidForm.title'),
        content: hasValidationMessages 
          ? <span>
            {
              validationResult.messages.map((msg, idx) =>
              <p key={idx}>              
                {msg}
              </p>
            )
            }            
          </span>
        : t('commons.alerts.invalidForm.content')
        });        
    }
  };

  const allSteps = {
    [EL_STEP_DATA]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_DATA, steps)}
        >
          {t('components.easyLoading.steps.data.title')}
        </span>
      ),
      render: (
        <ELDataStep
          mode={mode}
        />
      )
    },
    [EL_STEP_CONNECTION]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_CONNECTION, steps)}
        >
          {t('components.easyLoading.steps.connection.title')}
        </span>
      ),
      render: <ELConnectionStep/>
    },
    [EL_STEP_FILTER]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_FILTER, steps)}
        >
          {t('components.easyLoading.steps.filter.title')}
        </span>
      ),
      render: (
        <ELFilterStep
          sdmxFilters={sourceFormat === EL_SOURCE_FORMAT_SDMX_EP}
        />
      )
    },
    [EL_STEP_STRUCTURE]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_STRUCTURE, steps)}
        >
          {t('components.easyLoading.steps.structure.title')}
        </span>
      ),
      render: (
        <ELStructureStep/>
      )
    },
    [EL_STEP_DIMENSIONS]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_DIMENSIONS, steps)}
        >
          {t('components.easyLoading.steps.dimensions.title')}
        </span>
      ),
      render: (
        <ELDimensionsStep/>
      )
    },
    [EL_STEP_ATTRIBUTES]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_ATTRIBUTES, steps)}
        >
          {t('components.easyLoading.steps.attributes.title')}
        </span>
      ),
      render: (
        <ELAttributesStep/>
      )
    },
    [EL_STEP_SEMANTIC]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_SEMANTIC, steps)}
        >
          {t('components.easyLoading.steps.semantic.title')}
        </span>
      ),
      render: (
        <ELSemanticStep/>
      )
    },
    [EL_STEP_MAPPING]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_MAPPING, steps)}
        >
          {t('components.easyLoading.steps.mapping.title')}
        </span>
      ),
      render: (
        <ELMappingStep
        />
      )
    },
    [EL_STEP_LOAD]: {
      title: (
        <span
          style={CSS_POINTER}
          onClick={() => setStep(EL_STEP_LOAD, steps)}
        >
          {t('components.easyLoading.steps.load.title')}
        </span>
      ),
      render: (
        <ELLoadStep
          mode={mode}
        />
      )
    }
  };

  if (!mode || (steps || []).length === 0) {
    return null;
  }

  const readIsFinish = astTerritorialCodelist && astTerritoryConceptScheme && astTerritoryConcept;

  if (!readIsFinish && astEnabled && userHasASTPermission) {
    return (
      <span></span>
    )
  }

  const ELIsEnabled =  astEnabled && userHasASTPermission && astTerritorialCodelist !== -1 && 
                       astTerritoryConceptScheme !== -1 && astTerritoryConcept !== -1;

  if (ELIsEnabled) {
    return (
      <Fragment>
        <div style={{marginBottom: MARGIN_MD, display: "flex", alignItems: "center"}}>
          <div style={{width: 160, paddingRight: 40}}>
            {steps.indexOf(step) > 0 && (
              <Button
                style={{width: "100%"}}
                onClick={() => setStep(steps[steps.indexOf(step) - 1], steps)}
              >
                <Icon type="left"/>
                {t("commons.buttons.goBack.title")}
              </Button>
            )}
          </div>
          <div style={{width: "calc(100% - 320px)"}}>
            <Steps size="small" current={steps.indexOf(step)}>
              {steps.map((s, i) => (
                <Steps.Step
                  key={i}
                  title={allSteps[s].title}
                  description={allSteps[s].description}
                />
              ))}
            </Steps>
          </div>
          <div style={{width: 160, paddingLeft: 40, display: "flex", justifyContent: "flex-end"}}>
            {steps.indexOf(step) < (steps.length - 1) && (
              <Button
                type="primary"
                style={{width: "100%"}}
                onClick={() => setStep(steps[steps.indexOf(step) + 1], steps)}
              >
                {t("commons.buttons.goForward.title")}
                <Icon type="right"/>
              </Button>
            )}
          </div>
        </div>
        <Card type="inner">
          <Row type="flex" justify="center">
            <Col style={{width: "100%", maxWidth: 1200}}>
              {allSteps[step].render}
            </Col>
          </Row>
        </Card>
      </Fragment>
    );
  }
  else {
    let errorTitle;
    let errorContent = `${t('components.easyLoading.error.ast.noAstConfig.content')} `;
    let hRef = `./#${PATH_NODES_CONFIGURATION}`;
    let link = t('components.easyLoading.error.ast.nodesConfigLink.label');

    if (!astEnabled) {
      errorTitle = t('components.easyLoading.error.ast.noAstConfig.title');
    } else if (!userHasASTPermission) {
      errorTitle = t('components.easyLoading.error.ast.notAllowedAgency.title');
      errorContent= `${t('components.easyLoading.error.ast.notAllowedAgency.content')} `;
      hRef = `./#${PATH_SET_PERMISSIONS}`;
      link = t('components.easyLoading.error.ast.userConfigLink.label');
    } else if (astTerritorialCodelist === -1) {
      errorTitle = t('components.easyLoading.error.ast.invalidTerritorialCodelist.title');      
    }else if (astTerritoryConceptScheme === -1) {
      errorTitle = t('components.easyLoading.error.ast.invalidTerritoryConceptScheme.title');      
    }
    else if (astTerritoryConcept === -1) {
      errorTitle = t('components.easyLoading.error.ast.invalidTerritoryConcept.title');      
    }
   
    return (
      <Call
        cb={() => Modal.error({
          title: errorTitle,
          content:
            <Fragment>
              {errorContent}
              <a
                href={hRef}
                style={{textDecoration: "underline"}}
              >
                {link}
              </a>
            </Fragment>
        })}
      >
        <span/>
      </Call>
    );
  }  
};

export default compose(
  translate(),
  connect(mapStateToProps, mapDispatchToProps)
)(EasyLoading);


// WEBPACK FOOTER //
// ./src/components/easy-loading/index.jsx